import { CERTIFICATE_PREVIEW_LABELS } from '../../constants/labels';
import type { CertificateTemplateSelectedFormField } from '../../types';

const EQUIPMENT_PLACEHOLDER_FIELDS = [
  CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_NAME,
  CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_ID,
  CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_MODEL,
  CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_SERIAL,
  CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_PRODUCTION_DATE,
  CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_FIELD_SERVICE_TYPE,
];

const DetailCard = ({
  children,
  fixedHeight,
}: {
  children: React.ReactNode;
  /** When set, the card is locked to this pixel height and overflow is clipped. */
  fixedHeight?: number;
}) => (
  <div
    className="w-full rounded-[10px] border-l-4 border-[#2A4455] p-6 overflow-hidden"
    style={{
      background:
        'linear-gradient(to right, rgba(232, 238, 240, 0.44) 0%, rgba(209, 220, 225, 0.07) 80%)',
      ...(fixedHeight !== undefined ? { height: fixedHeight } : {}),
    }}
  >
    <div className="grid grid-cols-2 gap-x-28 gap-y-4 w-full text-[14px] leading-5 tracking-[0.25px] text-[#474A4C]">
      {children}
    </div>
  </div>
);

// Each subfield occupies a fixed-height slot (up to two clamped lines) so card
// heights stay uniform and pagination is deterministic.
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <p className="min-w-0 h-10 overflow-hidden line-clamp-2 break-words">
    {label}: {value}
  </p>
);

/** Header + subtitle + client/facility block (the top of the certificate body). */
export const CertificateIntro = ({
  headerText,
  subtitleText,
}: {
  headerText: string;
  subtitleText: string;
}) => (
  <div className="flex flex-col gap-12">
    {/* Header + subtitle */}
    <div className="flex flex-col gap-6 items-center">
      <div className="flex flex-col gap-2 items-center w-full">
        <p className="font-roboto font-medium text-[36px] leading-11 text-center text-[#181A1B] break-all">
          {headerText}
        </p>
        <div
          className="h-1 w-64 rounded-full"
          style={{
            background:
              'linear-gradient(to right, rgba(250, 212, 25, 0.2) 0%, #FFCC00 50%, rgba(250, 212, 25, 0.2) 100%)',
          }}
        />
      </div>
      <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-center text-[#474A4C] w-3xl wrap-break-word">
        {subtitleText}
      </p>
    </div>

    {/* Client + Facility */}
    <div className="flex items-start justify-between text-left">
      <div className="flex flex-col w-76.75">
        <p className="font-roboto font-medium text-[14px] leading-5 tracking-[0.25px] text-[#181A1B]">
          {CERTIFICATE_PREVIEW_LABELS.CLIENT_NAME_PLACEHOLDER}
        </p>
        <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-[#474A4C] whitespace-pre-line">
          {CERTIFICATE_PREVIEW_LABELS.CLIENT_EMAIL_PLACEHOLDER}
          {'\n'}
          {CERTIFICATE_PREVIEW_LABELS.CLIENT_PHONE_PLACEHOLDER}
        </p>
      </div>
      <div className="flex flex-col gap-2 w-76.75">
        <div className="flex flex-col">
          <p className="font-roboto font-medium text-[14px] leading-5 tracking-[0.25px] text-[#181A1B]">
            {CERTIFICATE_PREVIEW_LABELS.FACILITY_NAME_PLACEHOLDER}
          </p>
          <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-[#474A4C] whitespace-pre-line">
            {CERTIFICATE_PREVIEW_LABELS.FACILITY_ADDRESS_PLACEHOLDER}
          </p>
        </div>
        <p className="font-roboto text-[14px] leading-5 tracking-[0.25px] text-[#474A4C]">
          {CERTIFICATE_PREVIEW_LABELS.DATE_OF_ISSUE_LABEL}:{' '}
          {CERTIFICATE_PREVIEW_LABELS.DATE_OF_ISSUE_PLACEHOLDER}
        </p>
      </div>
    </div>
  </div>
);

/** Centered "Equipment data" section heading. */
export const EquipmentSectionTitle = () => (
  <div className="w-full text-center">
    <p className="font-roboto font-medium text-[22px] leading-7 text-[#181A1B]">
      {CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_SECTION_TITLE}
    </p>
  </div>
);

/** Equipment placeholder detail card (the fixed 6 equipment fields). */
export const EquipmentDetailCard = ({
  fixedHeight,
}: {
  fixedHeight?: number;
} = {}) => (
  <DetailCard {...(fixedHeight !== undefined ? { fixedHeight } : {})}>
    {EQUIPMENT_PLACEHOLDER_FIELDS.map((label) => (
      <DetailRow
        key={label}
        label={label}
        value={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_VALUE_PLACEHOLDER(label)}
      />
    ))}
  </DetailCard>
);

/** A detail card rendering up to 6 selected form fields as subfields. */
export const FormFieldsCard = ({
  fields,
  fixedHeight,
}: {
  fields: CertificateTemplateSelectedFormField[];
  fixedHeight?: number;
}) => (
  <DetailCard {...(fixedHeight !== undefined ? { fixedHeight } : {})}>
    {fields.map((field) => (
      <DetailRow
        key={field.id}
        label={field.label}
        value={CERTIFICATE_PREVIEW_LABELS.EQUIPMENT_VALUE_PLACEHOLDER(
          field.label
        )}
      />
    ))}
  </DetailCard>
);

interface CertificateContentProps {
  headerText: string;
  subtitleText: string;
  formFields: CertificateTemplateSelectedFormField[];
}

/**
 * The full certificate body, composed from its block pieces. Used directly for
 * a single-page render; {@link CertificatePreview} composes the same pieces
 * across pages when the content overflows a single A4 sheet.
 */
export const CertificateContent = ({
  headerText,
  subtitleText,
  formFields,
}: CertificateContentProps) => (
  <div className="flex flex-col gap-10">
    <CertificateIntro headerText={headerText} subtitleText={subtitleText} />
    <EquipmentSectionTitle />
    <div className="flex flex-col gap-8 w-full">
      <EquipmentDetailCard />
      {formFields.length > 0 && <FormFieldsCard fields={formFields} />}
    </div>
  </div>
);
